import React, { useContext, useState } from 'react'
import { Card, CardBody, CardHeader } from 'reactstrap'
import { store, actions } from '../../stores/multipost'
import SocialAccountProvider from './socialAccountProvider'
import { AlertCircle } from 'react-feather'
import { Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import ProviderMessage from './ProviderMessage'
import { TextareaWithSuggestions, SidIcon } from './TextareaWithSuggestions'

export default function Messages() {
  const { selectedAccounts, messages, selectedFiles, dispatch, aiSuggestionsPolicy, suggestions, promptLogIds } = useContext(store)
  const providerMessagesPresent = Object.keys(messages).filter(key => key !== 'default' && messages[key].value.length > 0).length > 0

  const [unifiedMessage, setUnifiedMessage] = useState(!providerMessagesPresent)
  const maxLength = Math.min(...selectedAccounts.map(account => SocialAccountProvider.find(account).maxCharsPerPost))

  const overLength = messages.default.overLength
  const length = messages.default.value.length
  const displayLength = overLength ? maxLength - length : length
  const hasStories = selectedFiles.some(f => f.assets.some(a => a.enabledActions.some(a => selectedAccounts.includes(a.provider) && a.story)))
  const hasInstagramAssets = selectedFiles.some(f => f.assets.some(a => a.enabledActions.some(a => a.provider == 'instagram' && !a.story)))

  const [requestedProviderSuggestions, setRequestedProviderSuggestions] = useState(false)

  const toggleUnifiedMessage = () => {
    if(unifiedMessage) {
      // set all provider messages to default if they are blank
      dispatch({
        type: actions.SET_MESSAGES,
        messages: {
          ...messages,
          ...selectedAccounts.reduce((acc, account) => {
            const message = messages[account].value || messages.default.value
            const overLength = message.length > SocialAccountProvider.find(account).maxCharsPerPost

            acc[account] = {
              value: message,
              overLength: overLength,
            }
            return acc
          }, {}),
        },
      })
    }
    setUnifiedMessage(!unifiedMessage)
  }

  let warnings = []

  if(length > 0 && hasStories) {
    warnings.push('Message will not be posted to Facebook or Instagram Stories')
  }

  if(selectedAccounts.includes('instagram') && !hasInstagramAssets && ((unifiedMessage && messages.default.value.length > 0) || messages.instagram.value.length > 0)) {
    warnings.push('Message will not be posted to Instagram without an image or video')
  }

  const onChange = (e) => {
    dispatch({
      type: actions.SET_MESSAGES,
      messages: {
        ...messages,
        default: {
          value: e.target.value,
          overLength: e.target.value.length > maxLength,
        },
      },
    })
  }

  const submittablePromptLogId =
    suggestions.default.promptLogId // user has suggestions and not dimissed them
    || promptLogIds.default // editing a scheduled share that previously requested suggestions

  return (
    <>
    <Card className="rounded-2 mb-4">
      <CardHeader className={`rounded-top-2 d-flex justify-content-between align-items-center ${!unifiedMessage && 'border-bottom-0'}`}>
        <UncontrolledDropdown>
          <DropdownToggle caret color="link" className="p-0">{unifiedMessage ? 'Message for all posts' : 'Message per account'}</DropdownToggle>
          <DropdownMenu className="p-0">
            <DropdownItem onClick={toggleUnifiedMessage}>Message for all posts</DropdownItem>
            <DropdownItem onClick={toggleUnifiedMessage}>Message per account</DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
        <div className="d-flex align-items-center" style={{height: '27px'}}>
          { unifiedMessage && selectedAccounts.length > 0 && (
            <div className={overLength ? 'text-danger' : 'text-muted'}>
              {displayLength}/{maxLength}
            </div>
          )}
          { aiSuggestionsPolicy?.can && !unifiedMessage && !requestedProviderSuggestions && (
            <div className="d-flex align-items-center overflow-hidden" style={{height: '43px'}}>
              <Button
                color="outline-muted arrow-right"
                className="text-dark"
                onClick={()=> {
                  setRequestedProviderSuggestions(true)
                  selectedAccounts.forEach(account => dispatch({ type: actions.FETCH_SUGGESTIONS, key: account }))
                }}
                disabled={selectedAccounts.length === 0}
              >
                Want a few suggestions?
                <span className="badge bg-gray-darker text-dark team-inset-button ms-2">
                  Generate
                </span>
              </Button>
              <SidIcon className="ms-1" />
            </div>
          ) }
        </div>
      </CardHeader>
      <CardBody className="p-0">
        { unifiedMessage ? (
          <>
            <TextareaWithSuggestions
              id="messages-default"
              name="team_post[messages][default]"
              placeholder={selectedAccounts.length === 0 ? "Select at least one account to add a message" : "This message is optional…"}
              onChange={onChange}
              value={messages.default.value}
              disabled={selectedAccounts.length === 0}
              showSuggestions={aiSuggestionsPolicy?.can}
              suggestions={suggestions.default}
              resetSuggestions={()=> dispatch({ type: actions.RESET_SUGGESTIONS })}
              fetchSuggestions={()=> dispatch({ type: actions.FETCH_SUGGESTIONS })}
            />
            {submittablePromptLogId && (
              <input type="hidden" name="team_post[prompt_log_ids][default]" value={submittablePromptLogId} />
            )}
          </>
        ) : (
          selectedAccounts.map(provider => (
            <ProviderMessage key={provider} provider={provider} showSuggestionsTrigger={requestedProviderSuggestions} />
          ))
        )}
      </CardBody>
    </Card>
    { warnings.length > 0 && (
      warnings.map((warning, index) => (
        <div key={`warning-${index}`} className="rounded-2 p-2 bold mb-4 d-flex align-items-center" style={{ backgroundColor: '#E4E9EF' }}>
          <AlertCircle size="18" color="#37444B" />
          <span className="ms-2" style={{ color: '#37444B' }}>{warning}</span>
        </div>
      ))
    )}
    </>
  )
}